import { createFileRoute, Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { COIN_PRICE_CENTS, MIN_COINS, formatEuro, grossFromNet } from "@/lib/salonturbo";
import { Scissors, Check, ArrowRight, CalendarCheck2, MapPin, Wallet } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import salonInterior from "@/assets/salon-interior.jpg";

const FAQ = [
  {
    q: "Was kostet Werbung für einen Friseursalon mit SalonTurbo?",
    a: `Ein Coin kostet ${formatEuro(COIN_PRICE_CENTS)} netto (${formatEuro(grossFromNet(COIN_PRICE_CENTS))} inkl. 19 % MwSt.) und entspricht einem geboosteten Tag. Du startest mit ${MIN_COINS} Coins und lädst danach auf, wann du willst. Es gibt keine Grundgebühr und keine Mindestlaufzeit.`,
  },
  {
    q: "Brauche ich einen eigenen Google-Ads-Account?",
    a: "Nein. Die Kampagnen laufen über unser Werbekonto. Du musst nichts einrichten, nichts verknüpfen und keine Keywords pflegen.",
  },
  {
    q: "Wie schnell laufen die Anzeigen?",
    a: "Direkt nach dem Onboarding legen wir deine Kampagne an, damit sie geprüft und freigegeben ist. Wenn du einen Tag boostest, wird sie zu Tagesbeginn aktiviert und rechtzeitig vor Ladenschluss wieder gestoppt.",
  },
  {
    q: "Was passiert an Tagen ohne Boost?",
    a: "Dann läuft keine Werbung und es kostet dich nichts. Deine Salonseite bleibt online, nur ohne den Hinweis auf kurzfristig freie Termine.",
  },
  {
    q: "Brauche ich eine eigene Website?",
    a: "Nein. SalonTurbo erstellt eine eigene Salonseite mit deinen Bildern, Öffnungszeiten und einem gut sichtbaren Anruf-Button. Eine vorhandene Website oder ein Buchungslink kann zusätzlich eingebunden werden.",
  },
  {
    q: "Kann ich das auch als einzelner Stuhlmieter nutzen?",
    a: "Ja. Du hinterlegst deine eigenen Kontaktdaten und Öffnungszeiten und boostest genau die Tage, an denen dein eigener Kalender Luft hat.",
  },
];

export const Route = createFileRoute("/friseur-werbung")({
  head: () => ({
    meta: [
      { title: "Friseur-Werbung: tagesgenau statt Dauerkosten" },
      {
        name: "description",
        content:
          "Werbung für den Friseursalon ohne Agentur: Tag auswählen, lokale Suchanzeigen laufen automatisch. 1 Coin = 25 € netto = 1 geboosteter Tag, keine Laufzeit.",
      },
      { property: "og:title", content: "Friseur-Werbung: tagesgenau statt Dauerkosten" },
      {
        property: "og:description",
        content:
          "Lokale Werbung nur an den Tagen, an denen Termine frei sind. Ohne Agentur, ohne Mindestlaufzeit.",
      },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
    ],
  }),
  component: FriseurWerbung,
});

function FriseurWerbung() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
        <section className="mx-auto max-w-5xl px-4 pb-12 pt-12 sm:pt-16">
          <p className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 eyebrow">
            <Scissors className="size-3.5" /> Für Friseursalons &amp; Stuhlmieter
          </p>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-light leading-[1.1] sm:text-5xl">
            Friseur-Werbung, die freie Stühle füllt
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            Kurzfristige Lücken im Kalender lassen sich schwer über Social Posts oder Flyer füllen –
            sie kommen zu spät und erreichen nicht die Menschen, die gerade jetzt einen Termin
            suchen. SalonTurbo schaltet lokale Suchanzeigen genau an den Tagen, an denen du sie
            brauchst, und stoppt sie automatisch wieder.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Button asChild size="lg">
              <Link to="/auth">
                Salon einrichten <ArrowRight className="ml-1 size-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/friseur-termin-heute">Wie Kunden spontan suchen</Link>
            </Button>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-4 pb-14">
          <div className="overflow-hidden rounded-3xl shadow-soft-lg">
            <img
              src={salonInterior}
              alt="Friseursalon mit freien Stühlen am Fenster"
              width={1280}
              height={960}
              loading="lazy"
              className="h-56 w-full object-cover sm:h-80"
            />
          </div>
        </section>

        <section className="border-y bg-secondary/50">
          <div className="mx-auto max-w-4xl px-4 py-16">
            <h2 className="font-display text-2xl font-normal sm:text-4xl">
              Was Salons meist machen – und was tagesgenaue Anzeigen anders machen
            </h2>
            <div className="mt-8 overflow-hidden rounded-3xl border bg-card">
              <table className="w-full text-left text-sm">
                <thead className="bg-secondary/60 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th scope="col" className="px-4 py-3 font-medium">
                      Kanal
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium">
                      Wirkt wann?
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium">
                      Kosten
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {[
                    {
                      k: "Instagram-Post / Story",
                      w: "Erreicht vor allem bestehende Follower, meist erst später am Tag",
                      c: "Zeitaufwand, oft Rabatte",
                    },
                    {
                      k: "Flyer &amp; Aushang",
                      w: "Wirkt langfristig, nicht für einen einzelnen Tag steuerbar",
                      c: "Druck- und Verteilkosten",
                    },
                    {
                      k: "Marketing-Agentur",
                      w: "Läuft dauerhaft, Anpassungen brauchen Abstimmung",
                      c: "Monatlicher Retainer",
                    },
                    {
                      k: "SalonTurbo",
                      w: "Nur an den Tagen, die du auswählst – bis zum Ladenschluss",
                      c: `${formatEuro(COIN_PRICE_CENTS)} netto pro Tag`,
                    },
                  ].map((row) => (
                    <tr key={row.k}>
                      <td className="px-4 py-4 font-medium">{row.k.replace("&amp;", "&")}</td>
                      <td className="px-4 py-4 text-muted-foreground">{row.w}</td>
                      <td className="px-4 py-4 text-muted-foreground">{row.c}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-4 py-16">
          <h2 className="font-display text-2xl font-normal sm:text-4xl">So funktioniert es</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-3">
            {[
              {
                icon: MapPin,
                title: "Salonprofil anlegen",
                text: "Name, Adresse, Telefonnummer, Öffnungszeiten und Bilder – daraus entsteht deine Salonseite mit Anruf-Button.",
              },
              {
                icon: CalendarCheck2,
                title: "Tage auswählen",
                text: "Im Kalender markierst du die Tage, an denen noch Termine frei sind – bis zu 30 Tage im Voraus.",
              },
              {
                icon: Wallet,
                title: "Anzeigen laufen automatisch",
                text: "Pro Tag wird ein Coin eingelöst. Die Anzeigen starten zu Tagesbeginn und enden vor Ladenschluss.",
              },
            ].map((step, index) => (
              <div key={step.title} className="rounded-2xl border bg-card p-6">
                <span className="eyebrow">
                  Schritt {index + 1}
                </span>
                <step.icon className="mt-3 size-6 text-primary" />
                <h3 className="mt-3 font-display text-lg font-medium">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="border-y bg-secondary/50">
          <div className="mx-auto max-w-4xl px-4 py-16">
            <h2 className="font-display text-2xl font-normal sm:text-4xl">Was es kostet</h2>
            <div className="mt-8 rounded-3xl border bg-card p-6 sm:p-8">
              <p className="font-display text-3xl font-normal sm:text-4xl">
                1 Coin = {formatEuro(COIN_PRICE_CENTS)} netto
                <span className="text-base font-normal text-muted-foreground"> = 1 Boost-Tag</span>
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                Preise zzgl. MwSt. ({formatEuro(grossFromNet(COIN_PRICE_CENTS))} inkl. 19 % MwSt.)
              </p>
              <ul className="mt-6 space-y-3 text-sm sm:text-base">
                {[
                  `Start ab ${MIN_COINS} Coins, danach lädst du auf, wann du willst.`,
                  "Coins verfallen nicht – du nutzt sie an den Tagen mit Lücken.",
                  "Keine Grundgebühr, keine Vertragsbindung, kein Setup-Fee.",
                  "An Tagen ohne Boost entstehen keine Werbekosten.",
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <Check className="mt-0.5 size-4 shrink-0 text-success" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-3xl px-4 py-16">
          <h2 className="font-display text-2xl font-normal sm:text-4xl">Häufige Fragen</h2>
          <Accordion type="single" collapsible className="mt-6">
            {FAQ.map((item, index) => (
              <AccordionItem key={item.q} value={`faq-${index}`}>
                <AccordionTrigger className="text-left">{item.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <section className="border-t bg-secondary/50">
          <div className="mx-auto max-w-4xl px-4 py-16">
            <div className="rounded-3xl bg-card p-6 sm:p-8">
              <h2 className="font-display text-2xl font-normal sm:text-3xl">
                Freie Tage gezielt bewerben
              </h2>
              <p className="mt-3 text-sm text-muted-foreground">
                In wenigen Minuten eingerichtet – danach reicht ein Klick pro Tag.
              </p>
              <Button asChild size="lg" className="mt-6 w-full sm:w-auto">
                <Link to="/auth">SalonTurbo einrichten</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
